'use client';

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAuth } from '@/lib/auth';
import { 
  SessionsApi, 
  TenantsApi,
  CasesApi,
  SecurityDashboardApi,
  TenantEnrollmentApi,
  AuthenticationApi,
  Configuration,
  CustomersApi,
  UsersApi,
  RolesApi,
  GroupsApi,
  AuditApi,
  TenantRelationshipsApi,
  CustomerDomainManagementApi,
  TenantDomainBindingsApi,
  XsoarIntegrationControllerApi,
  UserManagementConfigurationApi,
  ConfigurationManagementApi,
  PublishedMDRDashboardApi,
  EntitlementAdminApi,
  FeatureAdminApi,
  FeaturesApi
} from '@/lib/generated-api';
import { type AuthToken } from '@/lib/case-types';
import { createCsrfEnabledConfig } from './csrf-enabled-client';
import { normalizeApiBaseUrl, getDefaultApiBaseUrl } from './utils/url-normalization';
import { logger } from './logger';

interface ApiLibServices {
  TenantService?: any;
  SessionService?: any;
  SecurityCaseService?: any;
  SecurityDashboardService?: any;
  TenantEnrollmentService?: any;
  AuthenticationService?: any;
  CustomersService?: any;
  UsersService?: any;
  RolesService?: any;
  GroupsService?: any;
  AuditService?: any;
  TenantRelationshipsService?: any;
  CustomerDomainManagementService?: any;
  TenantDomainBindingsService?: any;
  XsoarIntegrationService?: any;
  UserManagementConfigurationService?: any;
  ConfigurationManagementService?: any;
  PublishedMDRDashboardService?: PublishedMDRDashboardApi;
  FeatureFlagService?: any;
  EntitlementAdminService?: any;
  FeatureAdminService?: any;
  FeaturesService?: any;
}

interface ApiLibTypes {
  [key: string]: any;
}

/** Stale module cache / old globalServices can omit newer auth-scoped clients; never treat as complete without these. */
function authScopedApiFactoryComplete(services: ApiLibServices | null | undefined): boolean {
  return Boolean(services?.PublishedMDRDashboardService && services?.SecurityCaseService)
}

// Global service cache to prevent multiple instances
let globalServices: ApiLibServices | null = null;
let globalServicesAuthToken: string | null = null;
const globalServicesInitialized = false;

// Helper function to initialize services without auth token
const initializeServicesWithoutAuth = async (): Promise<ApiLibServices> => {
  const loadedServices: ApiLibServices = {};

  // Use centralized URL normalization for base paths
  const { apiBase: apiBaseURL } = normalizeApiBaseUrl(getDefaultApiBaseUrl());
  const baseConfig = new Configuration({
    basePath: apiBaseURL,
    headers: {
      'Content-Type': 'application/json'
    }
  });
  
  // Configuration for AuthenticationService - omit credentials to prevent sending session cookies during sign-in
  const authConfig = new Configuration({
    basePath: apiBaseURL,
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'omit' // Don't send cookies for unauthenticated auth requests
  });
  
  loadedServices.TenantService = new TenantsApi(baseConfig);
  logger.log(' TenantService initialized with generated API');

  loadedServices.AuthenticationService = new AuthenticationApi(authConfig); // Use authConfig instead of baseConfig
  logger.log(' AuthenticationService initialized with generated API');

  // Do not create any tenant- or session-scoped services without auth. They require Authorization + X-Session-Id
  // (CsrfMiddleware in createCsrfEnabledConfig). Creating them with baseConfig caused GET /v1/cases and
  // select-tenants to run without auth/session (403 / "Session has expired"). These are only created in
  // initializeServicesWithAuth when authToken is present.
  logger.log(' SessionService, SecurityCaseService, TenantEnrollmentService, CustomersService, UsersService, RolesService, GroupsService, AuditService, TenantRelationshipsService, CustomerDomainManagementService, TenantDomainBindingsService, XsoarIntegrationService, UserManagementConfigurationService, ConfigurationManagementService, PublishedMDRDashboardService, EntitlementAdminService, FeatureAdminService, FeaturesService skipped (require auth/session)');
  
  return loadedServices;
};

// Helper function to initialize services with auth token
const initializeServicesWithAuth = async (authToken?: AuthToken): Promise<ApiLibServices> => {
  // Use centralized URL normalization utility
  const { apiBase: apiBaseURL } = normalizeApiBaseUrl(getDefaultApiBaseUrl());
  
  // Create API configuration with CSRF middleware if auth token is available
  const config = authToken 
    ? createCsrfEnabledConfig(authToken.access_token, apiBaseURL)
    : new Configuration({
        basePath: apiBaseURL,
        headers: {
          'Content-Type': 'application/json'
        }
      });
  
  logger.log(' [SERVICE-FACTORY] Initializing services with auth token:', {
    hasAuthToken: !!authToken,
    authTokenType: typeof authToken,
    authTokenAccessToken: authToken?.access_token ? 'present' : 'missing',
    configHeaders: config.headers
  });

  // Initialize services through generated API
  const loadedServices: ApiLibServices = {};
  
  loadedServices.TenantService = new TenantsApi(config);
  loadedServices.SessionService = new SessionsApi(config);
  loadedServices.SecurityCaseService = new CasesApi(config);
  loadedServices.SecurityDashboardService = new SecurityDashboardApi(config);
  loadedServices.PublishedMDRDashboardService = new PublishedMDRDashboardApi(config);
  loadedServices.TenantEnrollmentService = new TenantEnrollmentApi(config);
  loadedServices.AuthenticationService = new AuthenticationApi(config);
  loadedServices.CustomersService = new CustomersApi(config);
  loadedServices.UsersService = new UsersApi(config);
  loadedServices.RolesService = new RolesApi(config);
  loadedServices.GroupsService = new GroupsApi(config);
  loadedServices.AuditService = new AuditApi(config);
  loadedServices.TenantRelationshipsService = new TenantRelationshipsApi(config);
  loadedServices.CustomerDomainManagementService = new CustomerDomainManagementApi(config);
  loadedServices.TenantDomainBindingsService = new TenantDomainBindingsApi(config);
  loadedServices.XsoarIntegrationService = new XsoarIntegrationControllerApi(config);
  loadedServices.UserManagementConfigurationService = new UserManagementConfigurationApi(config);
  loadedServices.ConfigurationManagementService = new ConfigurationManagementApi(config);
  loadedServices.EntitlementAdminService = new EntitlementAdminApi(config);
  loadedServices.FeatureAdminService = new FeatureAdminApi(config);
  loadedServices.FeaturesService = new FeaturesApi(config);
  
  return loadedServices;
};

export function useApiLib() {
  const [services, setServices] = useState<ApiLibServices>({});
  const [types, setTypes] = useState<ApiLibTypes>({});
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const isLoadingRef = useRef(false);
  const isInitializedRef = useRef(false);

  const loadServices = useCallback(async () => {
    if (isLoadingRef.current || isInitializedRef.current) return;
    isLoadingRef.current = true;
    try {
      if (globalServices) {
        setServices(globalServices);
        setIsLoaded(true);
        isInitializedRef.current = true;
        return;
      }
      const loadedServices = await initializeServicesWithoutAuth();
      globalServices = loadedServices;
      globalServicesAuthToken = 'no-token';
      setServices(loadedServices);
      setIsLoaded(true);
      isInitializedRef.current = true;
    } catch (serviceError) {
      console.error('Failed to initialize services:', serviceError);
      setError(serviceError instanceof Error ? serviceError : new Error('Failed to initialize services'));
    } finally {
      isLoadingRef.current = false;
    }
  }, []);

  const initializeServices = useCallback(async (authToken?: AuthToken) => {
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;
    try {
      const currentAuthToken = authToken ? JSON.stringify(authToken) : 'no-token';
      const hasAuthServices = globalServices?.EntitlementAdminService != null;
      const skipReinit =
        globalServices &&
        globalServicesAuthToken === currentAuthToken &&
        (authToken != null || hasAuthServices) &&
        authScopedApiFactoryComplete(globalServices);
      if (skipReinit && globalServices) {
        setServices(globalServices);
        setIsLoaded(true);
        return;
      }
      if (authToken && globalServicesAuthToken !== currentAuthToken) {
        globalServices = null;
        globalServicesAuthToken = null;
      }
      const loadedServices = await initializeServicesWithAuth(authToken);
      globalServices = loadedServices;
      globalServicesAuthToken = currentAuthToken;
      setServices(loadedServices);
      setIsLoaded(true);
    } catch (serviceError) {
      console.error('Failed to initialize services:', serviceError);
      setError(serviceError instanceof Error ? serviceError : new Error('Failed to initialize services'));
    } finally {
      isLoadingRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (!isInitializedRef.current && !isLoadingRef.current) loadServices();
  }, [loadServices]);

  useEffect(() => {
    if (!isLoaded) return;
    const getStoredAuthToken = (): AuthToken | undefined => {
      const raw = sessionStorage.getItem('authToken') || localStorage.getItem('authToken');
      return raw ? (JSON.parse(raw) as AuthToken) : undefined;
    };
    const checkAuthToken = () => {
      const authToken = getStoredAuthToken()
      const currentAuthToken = authToken ? JSON.stringify(authToken) : 'no-token'
      const alreadyMatch = globalServices && globalServicesAuthToken === currentAuthToken
      const hasAuthServices = globalServices?.EntitlementAdminService != null
      if (alreadyMatch && (currentAuthToken !== 'no-token' || hasAuthServices)) {
        if (authToken && globalServices && !authScopedApiFactoryComplete(globalServices)) {
          globalServices = null
          globalServicesAuthToken = null
          void initializeServices(authToken)
          return
        }
        // Another useApiLib() instance may have run initializeServices; always sync local state from global.
        if (globalServices) setServices(globalServices)
        return
      }
      if (authToken) initializeServices(authToken)
      else initializeServices()
    }
    checkAuthToken();
    const handleStorageChange = (e: StorageEvent) => { if (e.key === 'authToken') checkAuthToken(); };
    const handleAuthTokenChanged = (e: Event) => {
      const detail = (e as CustomEvent<{ authToken?: AuthToken }>).detail?.authToken;
      if (detail) initializeServices(detail);
      else checkAuthToken();
    };
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('authTokenChanged', handleAuthTokenChanged);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('authTokenChanged', handleAuthTokenChanged);
    };
  }, [isLoaded, initializeServices]);

  return { services, types, isLoaded, error, initializeServices };
}

export function useTenantService() {
  const { services, isLoaded, error } = useApiLib();
  const [tenantService, setTenantService] = useState<any>(null);
  useEffect(() => {
    if (isLoaded && (services.TenantService || globalServices?.TenantService))
      setTenantService(services.TenantService || globalServices?.TenantService);
  }, [isLoaded, services.TenantService, globalServices?.TenantService]);
  const effectiveService = tenantService ?? services.TenantService ?? globalServices?.TenantService ?? null;
  return { tenantService: effectiveService, isLoaded, error };
}

export function useSessionService() {
  const { services, isLoaded, error } = useApiLib();
  const [sessionService, setSessionService] = useState<any>(null);
  useEffect(() => {
    if (isLoaded && (services.SessionService || globalServices?.SessionService))
      setSessionService(services.SessionService || globalServices?.SessionService);
  }, [isLoaded, services.SessionService, globalServices?.SessionService]);
  const effectiveService = sessionService ?? services.SessionService ?? globalServices?.SessionService ?? null;
  return { sessionService: effectiveService, isLoaded, error };
}

export function useSecurityCaseService() {
  const { services, isLoaded, error } = useApiLib()
  const [securityCaseService, setSecurityCaseService] = useState<any>(null)
  useEffect(() => {
    const svc = services.SecurityCaseService ?? globalServices?.SecurityCaseService
    if (isLoaded && svc) setSecurityCaseService(svc)
  }, [isLoaded, services.SecurityCaseService])
  const effective =
    securityCaseService ?? services.SecurityCaseService ?? globalServices?.SecurityCaseService ?? null
  return { securityCaseService: effective, isLoaded, error }
}

export function useSecurityDashboardService() {
  const { services, isLoaded, error } = useApiLib();
  const [securityDashboardService, setSecurityDashboardService] = useState<SecurityDashboardApi | null>(null);
  useEffect(() => {
    const service =
      (services.SecurityDashboardService || globalServices?.SecurityDashboardService) ?? null;
    if (isLoaded && service) setSecurityDashboardService(service);
    else if (isLoaded && !service) setSecurityDashboardService(null);
  }, [isLoaded, services.SecurityDashboardService, globalServices?.SecurityDashboardService]);
  const effectiveService =
    securityDashboardService ??
    services.SecurityDashboardService ??
    globalServices?.SecurityDashboardService ??
    null;
  return { securityDashboardService: effectiveService, isLoaded, error };
}

export function usePublishedMdrDashboardService() {
  const { services, isLoaded, error } = useApiLib();
  const [publishedMdrDashboardService, setPublishedMdrDashboardService] =
    useState<PublishedMDRDashboardApi | null>(null);
  useEffect(() => {
    const service =
      (services.PublishedMDRDashboardService || globalServices?.PublishedMDRDashboardService) ?? null;
    if (isLoaded && service) setPublishedMdrDashboardService(service);
    else if (isLoaded && !service) setPublishedMdrDashboardService(null);
  }, [isLoaded, services.PublishedMDRDashboardService, globalServices?.PublishedMDRDashboardService]);
  const effectiveService =
    publishedMdrDashboardService ??
    services.PublishedMDRDashboardService ??
    globalServices?.PublishedMDRDashboardService ??
    null;
  return { publishedMdrDashboardService: effectiveService, isLoaded, error };
}

export function useTenantEnrollmentService() {
  const { services, isLoaded, error } = useApiLib();
  const [tenantEnrollmentService, setTenantEnrollmentService] = useState<any>(null);
  useEffect(() => {
    if (!isLoaded) return;
    const service = services.TenantEnrollmentService || globalServices?.TenantEnrollmentService;
    if (service) setTenantEnrollmentService(service);
  }, [isLoaded, services.TenantEnrollmentService, globalServices?.TenantEnrollmentService]);
  const effectiveService =
    tenantEnrollmentService ??
    services.TenantEnrollmentService ??
    globalServices?.TenantEnrollmentService ??
    null;
  // Gate isLoaded on service presence so callers never see "loaded but no service"
  return { tenantEnrollmentService: effectiveService, isLoaded: isLoaded && !!effectiveService, error };
}

export function useAuthenticationService() {
  const { services, isLoaded, error } = useApiLib();
  const [authenticationService, setAuthenticationService] = useState<any>(null);
  useEffect(() => {
    if (isLoaded && (services.AuthenticationService || globalServices?.AuthenticationService))
      setAuthenticationService(services.AuthenticationService || globalServices?.AuthenticationService);
  }, [isLoaded, services.AuthenticationService, globalServices?.AuthenticationService]);
  const effectiveService =
    authenticationService ??
    services.AuthenticationService ??
    globalServices?.AuthenticationService ??
    null;
  return { authenticationService: effectiveService, isLoaded, error };
}

export function useCustomersService() {
  const { services, isLoaded, error } = useApiLib();
  const [customersService, setCustomersService] = useState<any>(null);
  useEffect(() => {
    if (isLoaded && (services.CustomersService || globalServices?.CustomersService))
      setCustomersService(services.CustomersService || globalServices?.CustomersService);
  }, [isLoaded, services.CustomersService, globalServices?.CustomersService]);
  // Derive effective service so UI gets it even before local state updates (avoids "isLoaded but no service" after nav/HMR)
  const effectiveService = customersService ?? services.CustomersService ?? globalServices?.CustomersService ?? null;
  return { customersService: effectiveService, isLoaded, error };
}

export function useUsersService() {
  const { services, isLoaded, error } = useApiLib();
  const [usersService, setUsersService] = useState<any>(null);
  useEffect(() => {
    if (isLoaded && (services.UsersService || globalServices?.UsersService))
      setUsersService(services.UsersService || globalServices?.UsersService);
  }, [isLoaded, services.UsersService, globalServices?.UsersService]);
  // Derive effective service so UI gets it even before local state updates (avoids
  // "isLoaded but no service" after nav/HMR — same pattern as useCustomersService).
  const effectiveService = usersService ?? services.UsersService ?? globalServices?.UsersService ?? null;
  return { usersService: effectiveService, isLoaded, error };
}

export function useRolesService() {
  const { services, isLoaded, error } = useApiLib();
  const [rolesService, setRolesService] = useState<any>(null);
  useEffect(() => {
    if (isLoaded && (services.RolesService || globalServices?.RolesService))
      setRolesService(services.RolesService || globalServices?.RolesService);
  }, [isLoaded, services.RolesService, globalServices?.RolesService]);
  const effectiveService = rolesService ?? services.RolesService ?? globalServices?.RolesService ?? null;
  return { rolesService: effectiveService, isLoaded, error };
}

export function useGroupsService() {
  const { services, isLoaded, error } = useApiLib();
  const [groupsService, setGroupsService] = useState<any>(null);
  useEffect(() => {
    if (isLoaded && (services.GroupsService || globalServices?.GroupsService))
      setGroupsService(services.GroupsService || globalServices?.GroupsService);
  }, [isLoaded, services.GroupsService, globalServices?.GroupsService]);
  const effectiveService = groupsService ?? services.GroupsService ?? globalServices?.GroupsService ?? null;
  return { groupsService: effectiveService, isLoaded, error };
}

export function useAuditService() {
  const { services, isLoaded, error } = useApiLib();
  const [auditService, setAuditService] = useState<any>(null);
  useEffect(() => {
    if (isLoaded && (services.AuditService || globalServices?.AuditService))
      setAuditService(services.AuditService || globalServices?.AuditService);
  }, [isLoaded, services.AuditService, globalServices?.AuditService]);
  // Derive effective service so UI gets it even before local state updates (avoids
  // "isLoaded but no service" after nav — same pattern as useCustomersService / useUsersService).
  const effectiveService = auditService ?? services.AuditService ?? globalServices?.AuditService ?? null;
  return { auditService: effectiveService, isLoaded, error };
}

export function useTenantRelationshipsService() {
  const { services, isLoaded, error } = useApiLib();
  const [tenantRelationshipsService, setTenantRelationshipsService] = useState<any>(null);
  useEffect(() => {
    if (isLoaded && (services.TenantRelationshipsService || globalServices?.TenantRelationshipsService)) {
      const service = services.TenantRelationshipsService || globalServices?.TenantRelationshipsService;
      if (service) setTenantRelationshipsService(service);
    }
  }, [isLoaded, services.TenantRelationshipsService, globalServices?.TenantRelationshipsService]);
  const effectiveService =
    tenantRelationshipsService ??
    services.TenantRelationshipsService ??
    globalServices?.TenantRelationshipsService ??
    null;
  return { tenantRelationshipsService: effectiveService, isLoaded, error };
}

export function useCustomerDomainManagementService() {
  const { services, isLoaded, error } = useApiLib();
  const [customerDomainManagementService, setCustomerDomainManagementService] = useState<any>(null);
  useEffect(() => {
    if (isLoaded && (services.CustomerDomainManagementService || globalServices?.CustomerDomainManagementService))
      setCustomerDomainManagementService(services.CustomerDomainManagementService || globalServices?.CustomerDomainManagementService);
  }, [isLoaded, services.CustomerDomainManagementService, globalServices?.CustomerDomainManagementService]);
  const effectiveService =
    customerDomainManagementService ??
    services.CustomerDomainManagementService ??
    globalServices?.CustomerDomainManagementService ??
    null;
  return { customerDomainManagementService: effectiveService, isLoaded, error };
}

export function useTenantDomainBindingsService() {
  const { services, isLoaded, error } = useApiLib();
  const [tenantDomainBindingsService, setTenantDomainBindingsService] = useState<any>(null);
  useEffect(() => {
    if (isLoaded && (services.TenantDomainBindingsService || globalServices?.TenantDomainBindingsService))
      setTenantDomainBindingsService(services.TenantDomainBindingsService || globalServices?.TenantDomainBindingsService);
  }, [isLoaded, services.TenantDomainBindingsService, globalServices?.TenantDomainBindingsService]);
  const effectiveService =
    tenantDomainBindingsService ??
    services.TenantDomainBindingsService ??
    globalServices?.TenantDomainBindingsService ??
    null;
  return { tenantDomainBindingsService: effectiveService, isLoaded, error };
}

export function useXsoarIntegrationService() {
  const { services, isLoaded, error } = useApiLib();
  const [xsoarIntegrationService, setXsoarIntegrationService] = useState<any>(null);
  useEffect(() => {
    if (isLoaded && (services.XsoarIntegrationService || globalServices?.XsoarIntegrationService))
      setXsoarIntegrationService(services.XsoarIntegrationService || globalServices?.XsoarIntegrationService);
  }, [isLoaded, services.XsoarIntegrationService, globalServices?.XsoarIntegrationService]);
  const effectiveService =
    xsoarIntegrationService ??
    services.XsoarIntegrationService ??
    globalServices?.XsoarIntegrationService ??
    null;
  return { xsoarIntegrationService: effectiveService, isLoaded, error };
}

export function useUserManagementConfigurationService() {
  const { services, isLoaded, error } = useApiLib();
  const [userManagementConfigurationService, setUserManagementConfigurationService] = useState<any>(null);
  useEffect(() => {
    if (isLoaded && (services.UserManagementConfigurationService || globalServices?.UserManagementConfigurationService))
      setUserManagementConfigurationService(services.UserManagementConfigurationService || globalServices?.UserManagementConfigurationService);
  }, [isLoaded, services.UserManagementConfigurationService, globalServices?.UserManagementConfigurationService]);
  const effectiveService =
    userManagementConfigurationService ??
    services.UserManagementConfigurationService ??
    globalServices?.UserManagementConfigurationService ??
    null;
  return { userManagementConfigurationService: effectiveService, isLoaded, error };
}

export function useConfigurationManagementService() {
  const { services, isLoaded, error } = useApiLib();
  const [configurationManagementService, setConfigurationManagementService] = useState<any>(null);
  useEffect(() => {
    if (isLoaded && (services.ConfigurationManagementService || globalServices?.ConfigurationManagementService))
      setConfigurationManagementService(services.ConfigurationManagementService || globalServices?.ConfigurationManagementService);
  }, [isLoaded, services.ConfigurationManagementService, globalServices?.ConfigurationManagementService]);
  const effectiveService =
    configurationManagementService ??
    services.ConfigurationManagementService ??
    globalServices?.ConfigurationManagementService ??
    null;
  return { configurationManagementService: effectiveService, isLoaded, error };
}

export function useEntitlementAdminService() {
  const { services, isLoaded, error } = useApiLib();
  const [entitlementAdminService, setEntitlementAdminService] = useState<any>(null);
  useEffect(() => {
    if (isLoaded && (services.EntitlementAdminService || globalServices?.EntitlementAdminService))
      setEntitlementAdminService(services.EntitlementAdminService || globalServices?.EntitlementAdminService);
  }, [isLoaded, services.EntitlementAdminService, globalServices?.EntitlementAdminService]);
  const effectiveService =
    entitlementAdminService ??
    services.EntitlementAdminService ??
    globalServices?.EntitlementAdminService ??
    null;
  return { entitlementAdminService: effectiveService, isLoaded, error };
}

export function useFeatureAdminService() {
  const { services, isLoaded, error } = useApiLib();
  const [featureAdminService, setFeatureAdminService] = useState<any>(null);
  useEffect(() => {
    if (isLoaded && (services.FeatureAdminService || globalServices?.FeatureAdminService))
      setFeatureAdminService(services.FeatureAdminService || globalServices?.FeatureAdminService);
  }, [isLoaded, services.FeatureAdminService, globalServices?.FeatureAdminService]);
  const effectiveService =
    featureAdminService ??
    services.FeatureAdminService ??
    globalServices?.FeatureAdminService ??
    null;
  return { featureAdminService: effectiveService, isLoaded, error };
}

export function useFeaturesService() {
  const { services, isLoaded, error } = useApiLib();
  const [featuresService, setFeaturesService] = useState<any>(null);
  useEffect(() => {
    if (isLoaded && (services.FeaturesService || globalServices?.FeaturesService))
      setFeaturesService(services.FeaturesService || globalServices?.FeaturesService);
  }, [isLoaded, services.FeaturesService, globalServices?.FeaturesService]);
  const effectiveService =
    featuresService ?? services.FeaturesService ?? globalServices?.FeaturesService ?? null;
  return { featuresService: effectiveService, isLoaded, error };
}

export function useUpdateAuthToken() {
  const { initializeServices } = useApiLib();
  const updateAuthToken = useMemo(() => (authToken: AuthToken) => initializeServices(authToken), [initializeServices]);
  return { updateAuthToken };
}

export function useComprehensiveCustomerService() {
  const { customersService, isLoaded: customersServiceLoaded } = useCustomersService();
  const { tenantEnrollmentService, isLoaded: tenantEnrollmentServiceLoaded } = useTenantEnrollmentService();
  const { session } = useAuth();
  const createCustomerWithTenant = useCallback(async (customerData: any, tenantData: any) => {
    if (!customersService || !tenantEnrollmentService || !session?.sessionId) throw new Error('Services not available');
    const customerResponse = await customersService.createCustomer({ sessionId: session.sessionId, createCustomerRequest: customerData }) as any;
    if (!customerResponse.success || !customerResponse.data) throw new Error(customerResponse.error || 'Failed to create customer');
    const enrollmentResponse = await tenantEnrollmentService.enrollTenant({
      tenantEnrollmentRequest: {
        organizationName: customerData.companyName || tenantData.domain.split('.')[0],
        domain: tenantData.domain,
        adminEmail: tenantData.adminEmail,
        adminFirstName: tenantData.adminFirstName,
        adminLastName: tenantData.adminLastName,
        adminPassword: tenantData.adminPassword,
        ssoProviderType: tenantData.ssoProviderType,
        providerConfig: tenantData.providerConfig || {},
        customerId: customerResponse.data.id,
        settings: { timezone: customerData.timezone || 'UTC', defaultLanguage: customerData.language || 'en', notificationPreferences: { emailAlerts: true, smsAlerts: false } }
      }
    }) as any;
    if (!enrollmentResponse.success) throw new Error(enrollmentResponse.error || 'Failed to create tenant');
    return { success: true, customer: customerResponse.data, tenantId: enrollmentResponse.tenantId, tenantName: enrollmentResponse.tenantName, enrollmentStatus: enrollmentResponse.enrollmentStatus, consentUrl: enrollmentResponse.consentUrl, message: 'Customer and tenant created successfully' };
  }, [customersService, tenantEnrollmentService, session?.sessionId]);
  return { createCustomerWithTenant, isLoaded: customersServiceLoaded && tenantEnrollmentServiceLoaded };
}

export function useFeatureFlagService() {
  const { services, isLoaded } = useApiLib();
  return { featureFlagService: services.FeatureFlagService, isLoaded: isLoaded && !!services.FeatureFlagService };
}

export type { AuthToken } from '@/lib/case-types';
